/**
 * Tell me when the operator comes back to this tab, and how long they were away.
 *
 * <p>Browsers throttle a hidden tab's timers and, after a few minutes, freeze it outright. While
 * frozen the STOMP heartbeat cannot run, so the socket can die without stompjs noticing; when the
 * operator returns, the page believes it is connected to a socket that no longer exists, and the
 * only thing that used to fix it was F5. The return to the tab is therefore the one moment worth
 * acting on: anything that happened while we were away has to be re-read now.
 *
 * <p>Fires on `visibilitychange` → visible and on window `focus`, de-duplicated so a return that
 * triggers both only calls back once. Passes the hidden duration in ms so callers can tell a quick
 * alt-tab (do nothing) from a long absence (assume the socket is a zombie).
 *
 * @returns an unsubscribe function
 */
export function onReturnToTab(callback) {
    let hiddenSince = document.visibilityState === "hidden" ? Date.now() : null;
    let lastFired = 0;

    const fire = () => {
        const now = Date.now();
        // Two events for one return (visibility, then focus) must not double-resync.
        if (now - lastFired < 1000) return;
        lastFired = now;
        const hiddenMs = hiddenSince ? now - hiddenSince : 0;
        hiddenSince = null;
        try {
            callback(hiddenMs);
        } catch (e) {
            console.warn("[visibility] return handler failed", e);
        }
    };

    const onVisibility = () => {
        if (document.visibilityState === "hidden") {
            hiddenSince = hiddenSince || Date.now();
        } else {
            fire();
        }
    };
    const onFocus = () => {
        // Focus without a prior hide is just clicking back into an already-visible window.
        if (hiddenSince) fire();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("focus", onFocus);
    };
}
